import React, { useCallback } from 'react';

import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Heading from '~/components/core/TextComponents/Heading';
import FinancesTable from '~/components/Finances/FinancesTable';
import LoadingIcon from '~/components/icons/LoadingIcon';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { Exposure } from '~/server_shared/swagger/data-contracts';

import { createPaymentNotification } from '../apiService';
import IncomeProtectionNotification from '../IncomeProtectionNotification';
import TEXTS from '../texts';
import type { IncomeProtectionDaSettingType } from '../types';

import IncomeProtectionAddPayment from './IncomeProtectionAddPayment';
import useIncomeProtectionScreenPaginatedPayments, {
  DISPLAY_COLUMN_IDS,
} from './useIncomeProtectionScreenPaginatedPayments';

interface IncomeProtectionDAScreenPaymentsProps {
  exposure: Exposure & { id: number };
  claim: ClaimPolymorph;
  paymentSetting: IncomeProtectionDaSettingType;
  medicalCertificateExpiryDate: string | null;
  isPaymentNotificationEnabled: boolean;
  onNotificationUpdate: () => Promise<unknown> | unknown;
}

const IncomeProtectionDAScreenPayments = ({
  exposure,
  claim,
  paymentSetting,
  medicalCertificateExpiryDate,
  isPaymentNotificationEnabled,
  onNotificationUpdate,
}: IncomeProtectionDAScreenPaymentsProps) => {
  const { data, isLoading, isFetching, reloadData, isError, paginationProps, filterParams, onSortByColumn } =
    useIncomeProtectionScreenPaginatedPayments({
      claim,
      exposure,
    });

  const onPaymentAdded = useCallback(() => {
    reloadData();
    onNotificationUpdate();
  }, [reloadData, onNotificationUpdate]);

  const createNotification = useCallback(
    (due_date: string) => {
      return createPaymentNotification({
        organizationId: claim.organization_id,
        claimId: claim.id,
        exposureId: exposure.id,
        dueDate: due_date,
      });
    },
    [claim.organization_id, claim.id, exposure.id]
  );

  const paymentNotification = paymentSetting.payment_notification;

  return (
    <div className="rounded-lg border-1 border-solid border-slate-500 p-24">
      <div className="mb-12 flex items-center justify-between">
        <Heading variant={Heading.TYPES.H2}>{TEXTS.paymentsSectionTitle}</Heading>
        <div className="flex items-center gap-12">
          <IncomeProtectionNotification
            existingNotification={paymentNotification}
            isNotificationTypeEnabled={isPaymentNotificationEnabled}
            createNotification={createNotification}
            onNotificationUpdate={onNotificationUpdate}
            notificationCreateDialogTitle={TEXTS.setPaymentNotificationDialogTitle}
            notificationCreateDialogSubtitle={TEXTS.setPaymentNotificationDialogContent}
          />
          <IncomeProtectionAddPayment
            paymentSetting={paymentSetting}
            medicalCertificateExpiryDate={medicalCertificateExpiryDate}
            exposure={exposure}
            claim={claim}
            onPaymentAdded={onPaymentAdded}
          />
        </div>
      </div>
      {isLoading || isError || !data ? (
        <SkeletonTable rowsCount={5} columnsCount={7} isError={isError} />
      ) : (
        <FinancesTable
          finances={data.finances}
          displayColumnIds={DISPLAY_COLUMN_IDS}
          paginationProps={paginationProps}
          sortByColumn={filterParams.sort_by_column}
          onSortByColumn={onSortByColumn}
          onReloadFinances={reloadData}
          counterViewText={
            isFetching ? <LoadingIcon iconColor="currentColor" className="animate-spin text-slate-600" /> : null
          }
        />
      )}
    </div>
  );
};

export default IncomeProtectionDAScreenPayments;
